import { t } from 'i18next'
import React from 'react'
import { Trans } from 'react-i18next'

import { InvoicingSystem, LisaAndValidationReason, SubscriptionStatus } from '../../common/enums.js'
import { BasicViewPaths, SubscriptionSteps } from '../../common/types.js'
import { ButtonAnchorProps } from '../components/button/button.js'
import { FooterColumnProps, FooterProps, FooterSocialItemProps } from '../components/footer/footer.js'
import { HeaderProps } from '../components/header/header.js'
import {
    CommunityIcon,
    FacebookIcon,
    InstagramIcon,
    LinkedinIcon,
    TwitterIcon,
    YoutubeIcon,
} from '../components/icon/icon.js'
import i18n from '../i18n.js'
import { Subscription } from '../models/subscription.js'
import { RootProps } from '../root.js'
import { FlowTypes, SubscriptionUuidViewPaths, View } from '../types.js'
import { FlowPickerProps } from '../views/flow-picker/flow-picker-step.js'
import { NotificationPageProps } from '../views/notification/notification.js'
import { getPortalFooterProps, getPortalRootProps } from '../views/portal/portal.js'
import { ViewPrivacyPolicyProps, ViewPrivacySection } from '../views/portal/privacy-policy/privacy-policy.js'
import { getLoadingProps } from './loading.js'
import { parseRoute } from './parse-route.js'
import { getSignRejectProps, getSignSuccessProps } from './signing.js'
import { getSubscriptionProps } from './subscription.js'

const getCreditFailedProps = (view: View, reason?: LisaAndValidationReason): NotificationPageProps => {
    return {
        title: t('errorPages.creditCheck.title'),
        button: {
            id: 'btn-credit-check-error',
            text: t('errorPages.creditCheck.button'),
            url: view.state.subscription?.rejectedRedirectUrl,
        },
        children:
            reason === LisaAndValidationReason.INCONCLUSIVE_CREDIT_RESULT
                ? t('errorPages.creditCheck.contentTryAgain')
                : t('errorPages.creditCheck.contentFailed'),
    }
}

const getAuthErrorProps = (view: View): NotificationPageProps => {
    return {
        title: t('errorPages.auth.title'),
        button: {
            id: 'btn-auth-error',
            text: t('errorPages.auth.button'),
            url: view.state.subscription?.rejectedRedirectUrl || 'https://shop.samsung.com/',
        },
        children: (
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore ReactI18N type definition issue
            <Trans i18nKey="errorPages.auth.content">
                <p>
                    <strong>Hej, tyvärr kan vi inte godkänna din ansökan. Vänligen välj ett annat betalsätt.</strong>
                </p>

                <p>
                    Det finns olika anledningar till att vi inte kan godkänna en ansökan. Har du frågor kring ditt
                    ärende, vänligen kontakta vår finansieringspartner Foxway som fattar beslutet på
                    subscriptions@foxway.com
                </p>
            </Trans>
        ),
    }
}

export const getPrivacyPolicyProps = (_view: View): ViewPrivacyPolicyProps => {
    return {
        title: t('privacyPolicy.title'),
        sections: t('privacyPolicy.sections', { returnObjects: true }) as unknown as ViewPrivacySection[],
    }
}

const getAccessoryErrorProps = (view: View): NotificationPageProps => {
    return {
        title: t('errorPages.accessory.title'),
        button: {
            id: 'btn-accessory-error',
            text: t('errorPages.accessory.button'),
            url: view.state.subscription?.rejectedRedirectUrl || 'https://shop.samsung.com/',
        },
        children: (
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore ReactI18N type definition issue
            <Trans i18nKey="errorPages.accessory.content">
                <p>
                    <strong>Add accessory error text</strong>
                </p>

                <p>Add accessory error text</p>
            </Trans>
        ),
    }
}

const getGeneralErrorProps = (view: View): NotificationPageProps => {
    return {
        title: t('errorPages.general.title'),
        button: view.state.subscription
            ? {
                  id: 'btn-general-error',
                  text: t('errorPages.general.button'),
                  url: view.state.subscription.abortedRedirectUrl,
              }
            : undefined,
        children: (
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore ReactI18N type definition issue
            <Trans i18nKey="errorPages.general.content">
                <p>
                    <strong>Add general error text</strong>
                </p>

                <p>Add general error text</p>
            </Trans>
        ),
    }
}

const getSubscriptionExistsProps = (view: View): NotificationPageProps => {
    return {
        title: t('existingSubscriptionStep.title'),
        button: {
            id: 'btn-change-existing-subscription',
            text: t('existingSubscriptionStep.buttonText'),
            isDisabled:
                !view.state.subscription || view.state.subscription.status === SubscriptionStatus.ONLYFORUPGRADE,
            onClick: () => {
                view.api.loadSubscription()
                const searchValue =
                    view.state.flowPicked === 'existing-user'
                        ? '/subscription-exists/' + view.state.subscription!.uuid + '/' + SubscriptionSteps.SIGN
                        : '/subscription-exists/'
                const replaceValue =
                    view.state.flowPicked === 'existing-user'
                        ? '/subscription/' + view.state.subscription!.uuid + '/' + SubscriptionSteps.PLANS
                        : '/subscription/'
                window.location.hash = `${window.location.hash.replace(searchValue, replaceValue)}`
            },
        },
        additionalInfo: <p>{t('existingSubscriptionStep.infoText')}</p>,
        children: <p>{t('existingSubscriptionStep.description')}</p>,
        activeSubscriptions: (view.state.subscription?.customer?.activeSubscriptions || []).map(
            (activeSubscriptionCard) => {
                if (activeSubscriptionCard.meta) {
                    activeSubscriptionCard.footer = {
                        title: activeSubscriptionCard.meta.product.plan.name,
                        description: (
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore ReactI18N type definition issue
                            <Trans
                                i18nKey="existingSubscriptionStep.activeFrom"
                                values={{
                                    date: activeSubscriptionCard.meta.activeDate,
                                }}
                                components={{ bold: <strong /> }}
                            >
                                Delivered at <strong>{activeSubscriptionCard.meta.activeDate}</strong>
                            </Trans>
                        ),
                        additionalDescription: activeSubscriptionCard.meta.eligibleDate ? (
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore ReactI18N type definition issue
                            <Trans
                                i18nKey="existingSubscriptionStep.eligibleFrom"
                                values={{
                                    date: activeSubscriptionCard.meta.eligibleDate,
                                }}
                                components={{ bold: <strong /> }}
                            >
                                Eligible for upgrade from <strong>{activeSubscriptionCard.meta.eligibleDate}</strong>
                            </Trans>
                        ) : (
                            ''
                        ),
                        // disclaimer: activeSubscriptionCard.meta.isEligible.result && (activeSubscriptionCard.meta.product.plan.code !== view.state.subscription?.mainProduct.plan.code) ? (
                        //     <Trans i18nKey="existingSubscriptionStep.planMismatchDisclaimer" values={{ name: activeSubscriptionCard.meta.product.plan.name }} components={{ bold: <strong /> }}>
                        //         Can only be upgraded into <strong>{activeSubscriptionCard.meta.product.plan.name}</strong>
                        //     </Trans>
                        // ) : '',
                        actions: [
                            {
                                text: i18n.t('existingSubscriptionStep.upgrade'),
                                isDisabled: !activeSubscriptionCard.meta.permissions?.canUpgrade,
                                onClick: () => {
                                    if (activeSubscriptionCard.meta?.permissions?.canUpgrade) {
                                        view.api
                                            .upgrade(activeSubscriptionCard.meta.subscriptionUuid)
                                            .then((response) => {
                                                if (response.statusCode !== 200) {
                                                    window.location.hash = `#/subscription/${activeSubscriptionCard.meta?.uuid}/${BasicViewPaths.GENERAL_ERROR}`
                                                    view.update()
                                                } else {
                                                    window.location.hash = `${window.location.hash.replace(
                                                        '/subscription-exists/',
                                                        '/subscription/',
                                                    )}`
                                                    view.update()
                                                }
                                            })
                                            .catch(() => {
                                                window.location.hash = `#/subscription/${activeSubscriptionCard.meta?.uuid}/${BasicViewPaths.GENERAL_ERROR}`
                                                view.update()
                                            })
                                    }
                                },
                            },
                        ],
                    }
                }

                return activeSubscriptionCard
            },
        ),
    }
}

const validateSubscriptionStep = (
    step: SubscriptionSteps,
    subscription: Subscription,
    view: View,
): SubscriptionSteps => {
    if (view.state.flowPicked === 'existing-user' && !subscription.customer) {
        return SubscriptionSteps.AUTH
    }

    if (step === SubscriptionSteps.VALIDATION) {
        if (subscription.canValidate()) {
            return SubscriptionSteps.VALIDATION
        } else if (subscription.canSign(view.state)) {
            return validateSubscriptionStep(SubscriptionSteps.SIGN, subscription, view)
        } else {
            return validateSubscriptionStep(SubscriptionSteps.CREDIT_CHECK, subscription, view)
        }
    }

    if (step === SubscriptionSteps.CREDIT_CHECK && subscription.canRunCreditCheck()) {
        if (subscription.invoicingSystem === InvoicingSystem.LISAAND) {
            // TODO: validate this with invoicingSystem check with Lisa&, but strong guess is they will take the customer personal number
            return SubscriptionSteps.PLANS
        } else {
            return SubscriptionSteps.CREDIT_CHECK
        }
    }

    if (step === SubscriptionSteps.SIGN) {
        if (subscription.invoicingSystem === InvoicingSystem.LISAAND) {
            return SubscriptionSteps.PLANS
        } else {
            if (subscription.canSign(view.state)) {
                return SubscriptionSteps.SIGN
            } else if (!subscription.customer?.personalNumber) {
                return SubscriptionSteps.CREDIT_CHECK
            }
        }
    }

    if (step === SubscriptionSteps.PLANS) {
        return SubscriptionSteps.PLANS
    }

    return SubscriptionSteps.AUTH
}

const getHeaderProps = (): HeaderProps => {
    return {
        url: `//shop.samsung.com/`,
        backButton: {
            element: 'a',
            text: t('header.back'),
            url: t('header.backLink'),
        },
        helpButton: {
            element: 'a',
            text: t('header.help'),
            url: t('header.helpLink'),
        },
    }
}

const getFooterProps = (isLisaAndFlow?: boolean): FooterProps => {
    const social = t('footer.social.items', { returnObjects: true }) as FooterSocialItemProps[]
    const terms = t('footer.terms', { returnObjects: true }) as ButtonAnchorProps[]

    const languageLink = t('footer.languageLink')
    const columns = t('footer.items', { returnObjects: true })

    return {
        copyright: t('footer.copyright', { currentYear: new Date().getFullYear() }),
        language: languageLink
            ? {
                  element: 'a',
                  text: t('footer.language'),
                  url: languageLink,
              }
            : undefined,
        topButton: {
            text: t('footer.backToTop'),
        },
        topNote: {
            text: t('footer.topNote.text'),
            button: {
                text: t('footer.topNote.button.text'),
                url: t('footer.topNote.button.url'),
            },
        },
        social: social?.length
            ? {
                  title: t('footer.social.title'),
                  items: social.map((item) => {
                      item.target = '_blank'

                      switch (item.text) {
                          case 'facebook':
                              item.icon = FacebookIcon
                              break
                          case 'twitter':
                              item.icon = TwitterIcon
                              break
                          case 'instagram':
                              item.icon = InstagramIcon
                              break
                          case 'linkedin':
                              item.icon = LinkedinIcon
                              break
                          case 'youtube':
                              item.icon = YoutubeIcon
                              break
                          case 'community':
                              item.icon = CommunityIcon
                              break
                      }

                      return item
                  }),
              }
            : undefined,
        terms: terms?.length ? terms : undefined,
        columns: columns as FooterColumnProps[],
        isLisaand: isLisaAndFlow,
    }
}

const getFlowPickerProps = (view: View): FlowPickerProps => {
    return {
        title: t('flowPicker.title'),
        optionCardGroupProps: {
            children: '',
            id: 'flow-picker-options',
            inputType: 'radio',
            name: 'flow-picker',
        },
        flowOptions: [
            {
                children: '',
                id: 'new-customer',
                value: 'new-user',
                labelText: t('flowPicker.newCustomer'),
            },
            {
                children: '',
                id: 'existing-customer',
                value: 'existing-user',
                labelText: t('flowPicker.existingCustomer'),
            },
        ],
        buttonProps: {
            text: t('flowPicker.nextButton'),
            isDisabled: !view.state.flowPicked,
        },
        onSubmit: (value: FlowTypes) => {
            localStorage.setItem('flowPicked-' + view.state.subscription!.uuid, value)
            view.state.flowPicked = value
            view.update()
        },
    }
}

export const getRootProps = (view: View): RootProps => {
    const rawRoute = window.location.hash.substring(2).split('?')[0] // Remove '#/' prefix and strip query params
    const route = parseRoute(rawRoute)
    const visualLocaleCode = view.state.portal.customer?.visualLocaleCode
    const isLisaAnd =
        !view.state.subscription?.invoicingSystem ||
        view.state.subscription?.invoicingSystem === InvoicingSystem.LISAAND

    let props: RootProps = {
        header: getHeaderProps(),
        footer: getFooterProps(isLisaAnd),
        portalFooter: getPortalFooterProps(),
        authRefresher: {
            authExp: view.state.authExp,
            onCancel: () => {
                if (view.state.authExp) {
                    view.api.portal
                        .logout()
                        .then(() => {
                            view.state.authExp = undefined
                            view.state.portal.customer = undefined
                            window.location.hash = `#/${visualLocaleCode}/login`
                        })
                        .catch(() => {
                            view.state.authExp = undefined
                            window.location.reload()
                        })
                }
            },
            onContinue: async () => {
                view.state.authExp = undefined
                await view.api.portal.refreshToken()
            },
        },
        banner: i18n.exists('banner.title')
            ? {
                  title: !isLisaAnd ? t('banner.title') : undefined,
                  subTitle: !isLisaAnd && i18n.exists('banner.subTitle') ? t('banner.subTitle') : undefined,
                  image: {
                      srcset: '../images/hero-banner-mobile@1x.png 644w, ../images/hero-banner-mobile@2x.png 1288w',
                      width: 644,
                      alt: 'banner image',
                      sources: [
                          {
                              minWidth: '801px',
                              srcset: '../images/hero-banner@1x.png 1440w, ../images/hero-banner@2x.png 2880w',
                          },
                      ],
                  },
              }
            : undefined,
    }

    // ANCHOR: Decision based on InvoicingSystem
    if (
        view.state.subscription &&
        view.state.subscription.invoicingSystem === InvoicingSystem.LISAAND &&
        !view.state.flowPicked &&
        !view.state.subscription?.customer
    ) {
        props.flowPickerProps = getFlowPickerProps(view)

        return props
    }

    switch (route.view) {
        case BasicViewPaths.CREDIT_FAILED:
            props.creditFailedProps = getCreditFailedProps(view)
            break
        case BasicViewPaths.GENERAL_ERROR:
            props.creditFailedProps = getGeneralErrorProps(view)
            break
        case BasicViewPaths.AUTH_ERROR:
            props.authErrorProps = getAuthErrorProps(view)
            break
        case BasicViewPaths.ACCESSORY_ERROR:
            props.authErrorProps = getAccessoryErrorProps(view)
            break
        case BasicViewPaths.PRIVACY_POLICY:
            props.privacyPolicyProps = getPrivacyPolicyProps(view)
            break

        case SubscriptionUuidViewPaths.SUBSCRIPTION_EXISTS:
            props.subscriptionsExist = getSubscriptionExistsProps(view)
            break

        case SubscriptionUuidViewPaths.SUBSCRIPTION:
            if (view.state.subscription) {
                if (view.state.subscription.hasCreditCheckFailed()) {
                    props.creditFailedProps = getCreditFailedProps(view, view.state.subscription.creditFailedReason)
                } else {
                    route.step = validateSubscriptionStep(route.step, view.state.subscription, view)
                    props.subscription = getSubscriptionProps(route, view.state.subscription, view)
                }
            } else {
                props.header = undefined
                props.footer = undefined
                props.banner = undefined
                props.loading = view.state.isLoading ? getLoadingProps() : undefined
            }
            break

        case BasicViewPaths.SIGN_SUCCESS:
            props.signSuccess = getSignSuccessProps()
            break

        case BasicViewPaths.SIGN_REJECT:
            props.signReject = getSignRejectProps()
            break

        case BasicViewPaths.REDIRECT_TO_SHOP:
            props.loading = getLoadingProps()
            window.location.href = 'https://shop.samsung.com/'
            break

        default:
            props = { ...props, ...getPortalRootProps(route, view, props) }
            break
    }

    if (view.state.notificationList) {
        props.notifications = {
            items: [...view.state.notificationList.items, ...(props.notifications?.items || [])],
        }
    }

    return props
}
